/**
=========================================================
* Trust Cargo - Accounting
=========================================================

* The office's books on one screen, one tab each:
*   overview, chart of accounts, receipt vouchers, payment vouchers, journal
*   entries, sales invoices,
*   purchase invoices, cash boxes & banks, categories, customers and
*   their groups & reps.
*
* The boxes, categories and the customer / supplier lists are loaded here
* once and handed to every tab, and reloaded whenever any tab saves - so a
* voucher saved on one tab shows in the box balance on the next.
*/

import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

import Overview from "./Overview";
import ChartTab from "./ChartTab";
import VouchersTab from "./VouchersTab";
import InvoicesTab from "./InvoicesTab";
import JournalTab from "./JournalTab";
import BoxesTab from "./BoxesTab";
import CategoriesTab from "./CategoriesTab";
import CustomersTab from "./CustomersTab";
import GroupsTab from "./GroupsTab";
import WorkingBar from "./WorkingBar";
import { api, problemOf } from "./api";

const TABS = [
  { key: "overview", label: "Overview · ملخص" },
  { key: "chart", label: "Chart of Accounts · شجرة الحسابات" },
  { key: "receipts", label: "Receipts · سندات قبض" },
  { key: "payments", label: "Payments · سندات صرف" },
  { key: "journal", label: "Journal · قيود اليومية" },
  { key: "sales", label: "Sales Invoices · فواتير مبيعات" },
  { key: "purchases", label: "Purchase Invoices · فواتير مشتريات" },
  { key: "boxes", label: "Boxes & Banks · صناديق ومصارف" },
  { key: "categories", label: "Categories · الفئات" },
  { key: "customers", label: "Customers · الزبائن" },
  { key: "groups", label: "Groups & Reps · المجموعات والمندوبين" },
];

const TAB_KEY = "accounting.tab";

function Accounting() {
  const [tab, setTab] = useState(() => {
    try {
      return sessionStorage.getItem(TAB_KEY) || "overview";
    } catch (e) {
      return "overview";
    }
  });
  const [lookups, setLookups] = useState({
    boxes: [],
    categories: [],
    parties: { customers: [], suppliers: [], vendors: [] },
    groups: [],
    reps: [],
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState("");

  const loadLookups = async () => {
    try {
      // groups and reps answer on their own - only two tabs need them, and
      // they must not take the rest of the page down with them
      const optional = (path) => api.get(path).catch(() => []);
      const [boxes, categories, parties, groups, reps] = await Promise.all([
        api.get("/boxes"),
        api.get("/categories"),
        api.get("/parties"),
        optional("/groups"),
        optional("/reps"),
      ]);
      setLookups({ boxes, categories, parties, groups, reps });
      setError("");
    } catch (e) {
      setError(problemOf(e, "Could not load accounting"));
    }
  };

  useEffect(() => {
    loadLookups();
  }, []);

  const changed = () => {
    loadLookups();
    setRefreshKey((k) => k + 1);
  };

  const pick = (key) => {
    setTab(key);
    try {
      sessionStorage.setItem(TAB_KEY, key);
    } catch (e) {
      // private mode - the tab just is not remembered
    }
  };

  return (
    <DashboardLayout>
      <WorkingBar />
      <SuiBox py={1}>
        <Card>
          <SuiBox display="flex" gap={1} flexWrap="wrap" px={3} pt={2.5}>
            {TABS.map((t) => (
              <Chip
                key={t.key}
                label={t.label}
                onClick={() => pick(t.key)}
                sx={({ palette, functions: { rgba } }) => ({
                  cursor: "pointer",
                  fontWeight: tab === t.key ? 700 : 500,
                  backgroundColor: tab === t.key ? rgba(palette.primary.main, 0.18) : "transparent",
                  border: `1px solid ${rgba(palette.dark.main, tab === t.key ? 0.25 : 0.12)}`,
                })}
              />
            ))}
          </SuiBox>

          {error && (
            <SuiBox px={3} pt={2}>
              <SuiTypography variant="button" color="error">
                {error}
              </SuiTypography>
            </SuiBox>
          )}

          {tab === "overview" && <Overview refreshKey={refreshKey} />}
          {tab === "chart" && <ChartTab />}
          {tab === "receipts" && (
            <VouchersTab type="receipt" lookups={lookups} onChanged={changed} />
          )}
          {tab === "payments" && (
            <VouchersTab type="payment" lookups={lookups} onChanged={changed} />
          )}
          {tab === "journal" && <JournalTab onChanged={changed} />}
          {tab === "sales" && <InvoicesTab type="sales" lookups={lookups} onChanged={changed} />}
          {tab === "purchases" && (
            <InvoicesTab type="purchase" lookups={lookups} onChanged={changed} />
          )}
          {tab === "boxes" && <BoxesTab boxes={lookups.boxes} onChanged={changed} />}
          {tab === "categories" && (
            <CategoriesTab categories={lookups.categories} onChanged={changed} />
          )}
          {tab === "customers" && (
            <CustomersTab groups={lookups.groups} reps={lookups.reps} onChanged={loadLookups} />
          )}
          {tab === "groups" && (
            <GroupsTab groups={lookups.groups} reps={lookups.reps} onChanged={loadLookups} />
          )}
        </Card>
      </SuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Accounting;
